/**
 * F42 (B1) — Admin venue management detail view.
 *
 * Route: /venues/:id
 * Permission: venues:read (every destructive affordance gates + hides by RBAC)
 *
 * The internal-staff venue-ops console for a single venue. Header Badges
 * (status / tier / claim / verification + suspended / flagged), then Tabs:
 *   - Profile: admin-editable name + per-field admin-lock toggle + If-Match
 *     (409 STALE_VENUE → StaleVenueDialog). Owner edits of a locked field are
 *     blocked server-side (F12 422 FIELD_ADMIN_LOCKED).
 *   - Bookings: read-through link to /bookings?venueId=
 *   - Staff: read-only deep-link to the owning B2B account members
 *   - Cross-feature: the 5 §2.5 deep-link cards (Subscription / Placements /
 *     Promotions / Tournaments / Financials[reserved F44])
 *   - Audit log: the venue_admin_audit timeline
 *   - Claims: claim summary + deep-link to the F11 venue-claims queue
 * + a destructive footer (Suspend / Restore / Flag / Merge), each RBAC-hidden.
 *
 * The richness comes from `adminVenuesApi.getDetail` (aliased to its fixture in
 * tests). Suspend/restore use the standalone SuspendDrawer (owner-notification
 * preview + confirm); the Claims tab mounts the venue-scoped ClaimsTab
 * (approve/reject/revoke + doc download); BulkPanel is mounted by the list page.
 * The V-07 "Status & tier" section (Profile tab, `venues:write`) exposes the
 * F10 lifecycle endpoints (publish draft→active, tier/claim/slug overrides).
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  type AuditEvent,
  type AuditEventKind,
  AuditLogTimeline,
  Badge,
  Banner,
  Button,
  Card,
  Dialog,
  EmptyState,
  FormField,
  OsmMapPicker,
  Modal,
  Select,
  Spinner,
  Stat,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Textarea,
  TextInput,
  TierBadge,
  useToast,
} from '@bravemove/ui-web';
import { SportIcon, type SportName } from '@bravemove/design-tokens/icons';
import type {
  AdminVenueDetail,
  AdminVenueProfile,
  HostedTournamentRow,
  VenueAdminAuditEntry,
  VenueClaimStatus,
  VenueStatus,
  VenueTier,
} from '@bravemove/types';
import {
  FLAG_REASON_MIN,
  SportNameSchema,
  VENUE_CLAIM_STATUSES,
  VENUE_STATUSES,
  VENUE_TIERS,
} from '@bravemove/types';
import { adminVenuesApi } from '../api/adminVenues';
import { adminVenueApi } from '../api/venue.js';
import { adminFetch } from '../auth/apiClient';
import { useAuth } from '../auth/useAuth';
import { translateApiError } from '../auth/errors.js';
import { venueVisibility, visibilityDotClass } from '../lib/venueVisibility';
import { VenueEditForm } from '../components/VenueEditForm';
import { VenueCancellationPolicyTab } from '../components/VenueCancellationPolicyTab';
import { ClaimsTab } from './venues/components/ClaimsTab';
import { AssignOwnerDialog } from './venues/components/AssignOwnerDialog';
import { SuspendDrawer, type SuspendDrawerMode } from './venues/components/SuspendDrawer';
// CST-003 — the F23 Commission tab component (import-only; the file is owned
// by the commission track). Mounted as an in-page tab below.
import { AdminVenueCommissionTab } from './venues/[venueId]/commission';
// The shared bookings list + staff members components — the SAME components the
// standalone /bookings page and the b2b-account Members tab render, embedded
// here (scoped to this venue) so a fix lands on every surface.
import { AdminBookingsList } from '../components/bookings/AdminBookingsList';
import { B2bAccountMembersTab } from './b2b-accounts/B2bAccountMembersTab';
import {
  EntityVerificationCard,
  VerificationStateCell,
} from '../components/verification/EntityVerificationCard';

const GMAPS_API_KEY = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined) ?? '';

const CREATED_BY_LABEL: Record<string, string> = {
  admin: 'Admin entry',
  user_submission: 'User submission',
  b2b_claim: 'B2B claim',
};

const SOURCE_LABEL: Record<string, string> = {
  google: 'Google',
  osm: 'OpenStreetMap',
  'google+osm': 'Google + OSM',
};

/** Tab keys synced to the `?tab=` query param so a refresh keeps the tab open. */
const VENUE_TABS = [
  'profile',
  'sportsCourts',
  'cancellationPolicy',
  'bookings',
  'staff',
  'deepLinks',
  'commission',
  'audit',
  'claims',
];

/** Build a Google Places photo media URL from a stored photo name (ref). */
function googlePhotoUrl(ref: string, maxHeightPx = 400): string {
  return `https://places.googleapis.com/v1/${ref}/media?maxHeightPx=${maxHeightPx}&key=${GMAPS_API_KEY}`;
}

const DAY_ORDER = [
  'mon',
  'tue',
  'wed',
  'thu',
  'fri',
  'sat',
  'sun',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

function titleCase(s: string): string {
  return s.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Base profile URLs for handle-style socials (website stores a full URL). */
const SOCIAL_BASE: Record<string, string> = {
  instagram: 'https://instagram.com/',
  facebook: 'https://facebook.com/',
  tiktok: 'https://tiktok.com/@',
};

function ensureHttpUrl(v: string): string {
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
}

/** Absolute, clickable href for a social entry. Socials carry a handle; the
 *  website carries a URL. A handle that's already a full URL is used as-is. */
function socialHref(platform: string, value: string): string {
  const v = value.trim();
  if (platform === 'website' || /^https?:\/\//i.test(v)) return ensureHttpUrl(v);
  const base = SOCIAL_BASE[platform];
  return base ? `${base}${v.replace(/^@/, '')}` : ensureHttpUrl(v);
}

/** Best-effort render of an opening-hours value (range array / {open,close} / closed). */
function formatHoursValue(v: unknown): string {
  if (v == null) return '—';
  if (
    typeof v === 'object' &&
    'closed' in (v as Record<string, unknown>) &&
    (v as { closed?: boolean }).closed
  ) {
    return 'Closed';
  }
  const range = (r: unknown): string | null => {
    if (r && typeof r === 'object') {
      const o = r as { open?: string; close?: string; from?: string; to?: string };
      const open = o.open ?? o.from;
      const close = o.close ?? o.to;
      if (open && close) return `${open}–${close}`;
    }
    return null;
  };
  if (Array.isArray(v)) {
    const parts = v.map(range).filter(Boolean);
    return parts.length ? parts.join(', ') : 'Closed';
  }
  return range(v) ?? 'Closed';
}

const STATUS_TONES: Record<string, 'success' | 'warning' | 'neutral' | 'danger' | 'info'> = {
  active: 'success',
  draft: 'neutral',
  suspended: 'warning',
  closed: 'danger',
  merged: 'info',
};

const CLAIM_TONES: Record<string, 'success' | 'warning' | 'neutral'> = {
  claimed: 'success',
  claim_pending: 'warning',
  unclaimed: 'neutral',
};

/** Map a `venue_admin_audit.action` onto an AuditLogTimeline kind tint. */
const AUDIT_KIND_MAP: Record<string, AuditEventKind> = {
  'venue.admin_edit': 'update',
  'venue.field_locked': 'permission',
  'venue.field_unlocked': 'permission',
  'venue.suspended': 'alert',
  'venue.restored': 'update',
  'venue.flagged': 'alert',
  'venue.flag_resolved': 'update',
  'venue.note': 'info',
  'venue.merged': 'update',
  'venue.merge_source': 'delete',
  'venue.bulk_suspended': 'alert',
  'venue.bulk_restored': 'update',
  'venue.bulk_flagged': 'alert',
};

function formatDate(iso: string | null, locale: string): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(iso),
    );
  } catch {
    return iso;
  }
}

type TranslateFn = ReturnType<typeof useI18n>['t'];

/**
 * The field(s) an audit entry touched. `fieldPath` is set only for single-field
 * edits; multi-field edits carry `fieldPath = null` but the changed keys live in
 * the after/before snapshot — so derive the list from there. This is why an edit
 * of e.g. phone + bookable now reads "phone, bookable" instead of nothing.
 */
function auditFieldLabel(entry: VenueAdminAuditEntry): string | undefined {
  if (entry.fieldPath) return entry.fieldPath;
  const snap = entry.afterSnapshot ?? entry.beforeSnapshot;
  if (snap && typeof snap === 'object' && !Array.isArray(snap)) {
    const keys = Object.keys(snap as Record<string, unknown>);
    if (keys.length > 0) return keys.join(', ');
  }
  return undefined;
}

function auditToEvent(entry: VenueAdminAuditEntry, t: TranslateFn): AuditEvent {
  const fieldLabel = auditFieldLabel(entry);
  return {
    id: entry.id,
    at: entry.createdAt,
    kind: AUDIT_KIND_MAP[entry.action] ?? 'info',
    summary: t(`admin:venue.f42.audit.action.${entry.action}`, { defaultValue: entry.action }),
    actor: { name: entry.actorDisplay ?? t('admin:venue.f42.audit.noReason') },
    ...(entry.reason ? { details: entry.reason } : {}),
    ...(fieldLabel ? { targetLabel: fieldLabel } : {}),
  };
}

// ─── Cross-feature deep-link cards ─────────────────────────────────────────────

function DeepLinkCard({
  id,
  title,
  description,
  href,
  available,
}: {
  id: string;
  title: string;
  description: string;
  href: string | null;
  available: boolean;
}) {
  const { t } = useI18n();
  return (
    <Card data-testid={`admin-venue-deeplink-${id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-[13px] font-semibold text-ink">{title}</h3>
          <p className="text-[12px] text-ink-3">{description}</p>
        </div>
        {available && href ? (
          <Link to={href}>
            <Button variant="secondary" size="xs">
              {t('admin:venue.f42.deepLinks.open')}
            </Button>
          </Link>
        ) : (
          <Button variant="ghost" size="xs" disabled>
            {t('admin:venue.f42.deepLinks.unavailable')}
          </Button>
        )}
      </div>
    </Card>
  );
}

/**
 * V-05 fix — the Tournaments card used to deep-link to a route that does not
 * exist (`/venues/:id/hosted-tournaments`). It now consumes the (previously
 * orphaned) `GET /admin/venues/:id/hosted-tournaments` endpoint inline and
 * links each hosted tournament to its REAL `/tournaments/:id` admin page.
 */
function HostedTournamentsCard({ venueId }: { venueId: string }) {
  const { t } = useI18n();
  const [rows, setRows] = useState<readonly HostedTournamentRow[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    adminVenuesApi
      .getHostedTournaments(venueId)
      .then((r) => {
        if (!cancelled) setRows(r);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [venueId]);

  return (
    <Card data-testid="admin-venue-deeplink-tournaments">
      <h3 className="text-[13px] font-semibold text-ink">
        {t('admin:venue.f42.deepLinks.tournaments')}
      </h3>
      <p className="text-[12px] text-ink-3">{t('admin:venue.f42.deepLinks.tournamentsDesc')}</p>
      <div className="mt-2">
        {failed ? (
          <p className="text-[12px] text-ink-3">
            {t('admin:venue.f42.deepLinks.hostedError', {
              defaultValue: 'Hosted tournaments could not be loaded.',
            })}
          </p>
        ) : rows === null ? (
          <Spinner size={16} />
        ) : rows.length === 0 ? (
          <p className="text-[12px] text-ink-3">{t('admin:venue.f42.deepLinks.hostedEmpty')}</p>
        ) : (
          <ul className="flex flex-col gap-1" data-testid="admin-venue-hosted-tournaments">
            {rows.map((row) => (
              <li key={row.tournamentId} className="flex items-center justify-between gap-2">
                <Link
                  to={row.deepLink}
                  className="min-w-0 truncate text-[12.5px] text-accent hover:underline"
                >
                  {row.title}
                </Link>
                <Badge tone="neutral" size="sm">
                  {row.status}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}

// ─── V-07 — Status & tier admin controls (the F10 lifecycle endpoints) ────────

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SLUG_RE = /^[a-z0-9-]+$/;

type LifecycleChangeKind = 'status' | 'tier' | 'claim' | 'slug';

/**
 * V-07 fix — the F42 rewrite dropped F10's tier/claim/status/slug override UI
 * without replacement, so a draft (e.g. scraped) venue could never be published
 * from the admin portal. This section re-exposes the existing F10 endpoints
 * (`PATCH /admin/venues/:id/{status,tier,claim-status,slug}`), each behind a
 * confirm dialog. Gated `venues:write` at the call site.
 */
function StatusTierAdminSection({
  detail,
  onChanged,
}: {
  detail: AdminVenueDetail;
  onChanged: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();

  const [status, setStatus] = useState(detail.status);
  const [tier, setTier] = useState(detail.tier);
  const [claimStatus, setClaimStatus] = useState(detail.claimStatus);
  const [ownerAccountId, setOwnerAccountId] = useState(detail.ownerB2bAccountId ?? '');
  const [slug, setSlug] = useState(detail.slug);
  const [confirm, setConfirm] = useState<LifecycleChangeKind | null>(null);
  const [busy, setBusy] = useState(false);
  const [assignOwnerOpen, setAssignOwnerOpen] = useState(false);

  // 'merged' is owned by the merge wizard (the status endpoint requires a
  // mergedIntoVenueId for it) — exclude it from the manual override options.
  const statusOptions = VENUE_STATUSES.filter((s) => s !== 'merged').map((s) => ({
    value: s,
    label: t(`admin:venue.status.${s}`, { defaultValue: s }),
  }));
  const tierOptions = VENUE_TIERS.map((v) => ({
    value: v,
    label: t(`admin:venue.tier.${v}`, { defaultValue: v }),
  }));
  const claimOptions = VENUE_CLAIM_STATUSES.map((c) => ({
    value: c,
    label: t(`admin:venue.claimStatus.${c}`, { defaultValue: c }),
  }));

  const ownerValid =
    claimStatus !== 'claimed' ||
    ownerAccountId.trim() === '' ||
    UUID_RE.test(ownerAccountId.trim());
  const slugValid = SLUG_RE.test(slug.trim()) && slug.trim().length <= 80;

  const CONFIRM_COPY: Record<LifecycleChangeKind, { title: string; body: string }> = {
    status: {
      title: t('admin:venue.f42.statusTier.confirmStatusTitle', { defaultValue: 'Change status?' }),
      body: t('admin:venue.f42.statusTier.confirmStatusBody', {
        defaultValue:
          'Changing the status controls public visibility. Draft → active publishes the venue.',
      }),
    },
    tier: {
      title: t('admin:venue.f42.statusTier.confirmTierTitle', { defaultValue: 'Change tier?' }),
      body: t('admin:venue.f42.statusTier.confirmTierBody', {
        defaultValue: 'Tier is the listing/claim tier (F11) — override only when you are sure.',
      }),
    },
    claim: {
      title: t('admin:venue.f42.statusTier.confirmClaimTitle', {
        defaultValue: 'Change claim status?',
      }),
      body: t('admin:venue.f42.statusTier.confirmClaimBody', {
        defaultValue:
          'Manually overrides the claim state. The normal path is the venue-claims queue.',
      }),
    },
    slug: {
      title: t('admin:venue.f42.statusTier.confirmSlugTitle', { defaultValue: 'Change slug?' }),
      body: t('admin:venue.f42.statusTier.confirmSlugBody', {
        defaultValue:
          'The old slug becomes an alias (301 redirect), so external links keep resolving.',
      }),
    },
  };

  const run = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      if (confirm === 'status') {
        await adminVenueApi.updateStatus(detail.id, status as VenueStatus);
        toast.push({ tone: 'success', title: t('admin:venue.detail.statusChanged') });
      } else if (confirm === 'tier') {
        await adminVenueApi.updateTier(detail.id, tier as VenueTier);
        toast.push({ tone: 'success', title: t('admin:venue.detail.tierChanged') });
      } else if (confirm === 'claim') {
        await adminVenueApi.updateClaimStatus(
          detail.id,
          claimStatus as VenueClaimStatus,
          claimStatus === 'claimed' ? ownerAccountId.trim() || undefined : null,
        );
        toast.push({ tone: 'success', title: t('admin:venue.detail.claimStatusChanged') });
      } else {
        await adminVenueApi.updateSlug(detail.id, slug.trim());
        toast.push({ tone: 'success', title: t('admin:venue.detail.slugChanged') });
      }
      setConfirm(null);
      onChanged();
    } catch (err) {
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="admin-venue-status-tier">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-[13px] font-semibold text-ink">
          {t('admin:venue.f42.statusTier.title', { defaultValue: 'Status & tier (admin)' })}
        </h3>
        {detail.status === 'draft' ? (
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              setStatus('active');
              setConfirm('status');
            }}
            data-testid="admin-venue-publish"
          >
            {t('admin:venue.f42.statusTier.publish', { defaultValue: 'Publish venue' })}
          </Button>
        ) : null}
      </div>
      <p className="mb-3 text-[12px] text-ink-3">
        {t('admin:venue.f42.statusTier.intro', {
          defaultValue:
            'Lifecycle overrides — each change is confirmed and audit-logged. Suspension has its own flow in the header.',
        })}
      </p>

      <div className="grid gap-3 sm:grid-cols-2">
        <FormField label={t('admin:venue.fields.tier', { defaultValue: 'Tier' })}>
          <div className="flex items-center gap-2">
            <Select
              value={tier}
              onChange={(e) => setTier(e.target.value)}
              options={tierOptions}
              data-testid="admin-venue-tier-select"
            />
            <Button
              variant="secondary"
              size="sm"
              disabled={tier === detail.tier}
              onClick={() => setConfirm('tier')}
              data-testid="admin-venue-tier-apply"
            >
              {t('admin:venue.f42.statusTier.apply', { defaultValue: 'Apply' })}
            </Button>
          </div>
        </FormField>

        <FormField label={t('admin:venue.fields.slug', { defaultValue: 'Slug' })}>
          <div className="flex items-center gap-2">
            <TextInput
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder={t('admin:venue.fields.slugPlaceholder', {
                defaultValue: 'lowercase-letters-and-hyphens',
              })}
              data-testid="admin-venue-slug-input"
            />
            <Button
              variant="secondary"
              size="sm"
              disabled={slug.trim() === detail.slug || !slugValid}
              onClick={() => setConfirm('slug')}
              data-testid="admin-venue-slug-apply"
            >
              {t('admin:venue.f42.statusTier.apply', { defaultValue: 'Apply' })}
            </Button>
          </div>
        </FormField>

        <FormField label={t('admin:venue.filters.status', { defaultValue: 'Status' })}>
          <div className="flex items-center gap-2">
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              options={statusOptions}
              data-testid="admin-venue-status-select"
            />
            <Button
              variant="secondary"
              size="sm"
              disabled={status === detail.status}
              onClick={() => setConfirm('status')}
              data-testid="admin-venue-status-apply"
            >
              {t('admin:venue.f42.statusTier.apply', { defaultValue: 'Apply' })}
            </Button>
          </div>
        </FormField>

        <FormField
          label={t('admin:venue.fields.claimStatus', { defaultValue: 'Claim status' })}
          helper={
            claimStatus === 'claimed'
              ? t('admin:venue.fields.ownerB2bAccountIdPlaceholder', {
                  defaultValue: 'UUID — leave blank if unclaimed',
                })
              : undefined
          }
        >
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Select
                value={claimStatus}
                onChange={(e) => setClaimStatus(e.target.value)}
                options={claimOptions}
                data-testid="admin-venue-claim-select"
              />
              <Button
                variant="secondary"
                size="sm"
                disabled={
                  (claimStatus === detail.claimStatus &&
                    (claimStatus !== 'claimed' ||
                      ownerAccountId.trim() === (detail.ownerB2bAccountId ?? ''))) ||
                  !ownerValid
                }
                onClick={() => setConfirm('claim')}
                data-testid="admin-venue-claim-apply"
              >
                {t('admin:venue.f42.statusTier.apply', { defaultValue: 'Apply' })}
              </Button>
            </div>
            {claimStatus === 'claimed' ? (
              <TextInput
                value={ownerAccountId}
                onChange={(e) => setOwnerAccountId(e.target.value)}
                placeholder={t('admin:venue.fields.ownerB2bAccountId', {
                  defaultValue: 'Owner B2B account ID',
                })}
                data-testid="admin-venue-claim-owner"
              />
            ) : null}
          </div>
        </FormField>

        <FormField
          label={t('admin:f43fc.assignOwner.owner_field_label', { defaultValue: 'Owner' })}
          helper={t('admin:f43fc.assignOwner.owner_field_helper', {
            defaultValue: 'Force-assign or clear the owning B2B account directly.',
          })}
        >
          <div className="flex items-center gap-2">
            <span
              className="min-w-0 flex-1 truncate font-mono text-[12px] text-ink-2"
              data-testid="admin-venue-owner-value"
            >
              {detail.ownerB2bAccountId ??
                t('admin:f43fc.assignOwner.owner_none', { defaultValue: '— unclaimed' })}
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setAssignOwnerOpen(true)}
              data-testid="admin-venue-assign-owner"
            >
              {t('admin:f43fc.assignOwner.title', { defaultValue: 'Assign owner' })}
            </Button>
          </div>
        </FormField>
      </div>

      <Dialog
        open={confirm !== null}
        onClose={() => setConfirm(null)}
        onConfirm={() => void run()}
        loading={busy}
        destructive={confirm === 'status' && status !== 'active'}
        title={confirm ? CONFIRM_COPY[confirm].title : ''}
        description={confirm ? CONFIRM_COPY[confirm].body : ''}
        confirmLabel={t('admin:venue.f42.statusTier.apply', { defaultValue: 'Apply' })}
        cancelLabel={t('admin:common.cancel', { defaultValue: 'Cancel' })}
      />

      {/* Assign / reassign / clear the owning B2B account directly (venues:write). */}
      <AssignOwnerDialog
        open={assignOwnerOpen}
        onClose={() => setAssignOwnerOpen(false)}
        currentOwnerB2bAccountId={detail.ownerB2bAccountId}
        onSubmit={async (input) => {
          await adminVenuesApi.reassignOwner(detail.id, input);
        }}
        onAssigned={() => {
          toast.push({
            tone: 'success',
            title: t('admin:f43fc.assignOwner.toast_assigned', { defaultValue: 'Owner updated' }),
          });
          onChanged();
        }}
      />
    </Card>
  );
}

// ─── Rich profile overview ─────────────────────────────────────────────────────

function InfoRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-hairline/60 py-1.5 last:border-0">
      <span className="shrink-0 text-[12px] text-ink-3">{label}</span>
      <span className="min-w-0 break-words text-end text-[12.5px] text-ink">{children}</span>
    </div>
  );
}

function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <h3 className="text-[13px] font-semibold text-ink">{title}</h3>
        {action}
      </div>
      {children}
    </Card>
  );
}

function VenueOverview({ profile }: { profile: AdminVenueProfile }) {
  const { t, locale } = useI18n();
  const dash = '—';
  // Each photo is a paid Places call, so they load only when asked for.
  const [showGooglePhotos, setShowGooglePhotos] = useState(false);
  const latN = profile.lat != null && profile.lat !== '' ? Number(profile.lat) : null;
  const lngN = profile.lng != null && profile.lng !== '' ? Number(profile.lng) : null;

  const regionLine = [profile.city.name, profile.caza.name, profile.governorate.name]
    .filter(Boolean)
    .join(', ');
  const socialEntries = Object.entries(profile.socialLinks ?? {}).filter(([, v]) => v);
  const websiteUrl = (profile.socialLinks?.website ?? '').trim();
  const handleEntries = socialEntries.filter(([k]) => k !== 'website');
  const photos = profile.photos.filter((p) => p.cdnUrl);
  const hoursEntries =
    profile.openingHours && typeof profile.openingHours === 'object'
      ? Object.entries(profile.openingHours as Record<string, unknown>).sort((a, b) => {
          const ai = DAY_ORDER.indexOf(a[0].toLowerCase());
          const bi = DAY_ORDER.indexOf(b[0].toLowerCase());
          return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
        })
      : [];

  return (
    <div className="pt-4" data-testid="admin-venue-overview">
      <div className="grid gap-3 lg:grid-cols-3">
        {/* Left — details */}
        <div className="space-y-3 lg:col-span-2">
          <Section title={t('admin:venue.f42.overview.about', { defaultValue: 'About' })}>
            <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-ink-2">
              {profile.description?.trim() ||
                t('admin:venue.f42.overview.noDescription', {
                  defaultValue: 'No description yet.',
                })}
            </p>
            {profile.sports.length > 0 ? (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {profile.sports.map((s) => (
                  <span
                    key={s.sportName}
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11.5px] font-medium ${
                      s.isPrimary ? 'bg-accent-soft text-accent' : 'bg-surface-2 text-ink-2'
                    }`}
                  >
                    {titleCase(s.sportName)}
                    {s.isPrimary ? ' ★' : ''}
                  </span>
                ))}
              </div>
            ) : null}
          </Section>

          <Section title={t('admin:venue.f42.overview.contact', { defaultValue: 'Contact' })}>
            <InfoRow label={t('admin:venue.fields.phone', { defaultValue: 'Phone' })}>
              {profile.phone ?? dash}
            </InfoRow>
            <InfoRow label={t('admin:venue.f42.overview.whatsapp', { defaultValue: 'WhatsApp' })}>
              {profile.whatsappPhone ?? dash}
            </InfoRow>
            <InfoRow label={t('admin:venue.f42.overview.website', { defaultValue: 'Website' })}>
              {websiteUrl ? (
                <a
                  href={socialHref('website', websiteUrl)}
                  target="_blank"
                  rel="noreferrer"
                  className="break-all text-accent hover:underline"
                  data-testid="admin-venue-website-link"
                >
                  {websiteUrl}
                </a>
              ) : (
                dash
              )}
            </InfoRow>
            <InfoRow
              label={t('admin:venue.f42.overview.socials', { defaultValue: 'Social links' })}
            >
              {handleEntries.length === 0 ? (
                dash
              ) : (
                <span className="inline-flex flex-wrap justify-end gap-2">
                  {handleEntries.map(([k, v]) => (
                    <a
                      key={k}
                      href={socialHref(k, v)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-accent hover:underline"
                      title={k}
                    >
                      {SOCIAL_BASE[k] ? `@${v.replace(/^@/, '')}` : v}
                    </a>
                  ))}
                </span>
              )}
            </InfoRow>
          </Section>

          <Section title={t('admin:venue.f42.overview.operations', { defaultValue: 'Operations' })}>
            <InfoRow label={t('admin:venue.f42.overview.bookable', { defaultValue: 'Bookable' })}>
              <Badge tone={profile.bookable ? 'success' : 'neutral'} size="sm">
                {profile.bookable
                  ? t('admin:common.yes', { defaultValue: 'Yes' })
                  : t('admin:common.no', { defaultValue: 'No' })}
              </Badge>
            </InfoRow>
            <InfoRow label={t('admin:venue.f42.overview.createdBy', { defaultValue: 'Source' })}>
              {profile.createdByKind
                ? (CREATED_BY_LABEL[profile.createdByKind] ?? profile.createdByKind)
                : dash}
            </InfoRow>
            <InfoRow label={t('admin:venue.f42.overview.tax', { defaultValue: 'Tax' })}>
              {profile.taxRatePercent != null
                ? `${profile.taxRatePercent}% (${profile.taxMode ?? dash})`
                : dash}
            </InfoRow>
            <InfoRow label={t('admin:venue.f42.overview.timezone', { defaultValue: 'Timezone' })}>
              {profile.timezone ?? dash}
            </InfoRow>
            <InfoRow label={t('admin:venue.f42.overview.created', { defaultValue: 'Created' })}>
              {formatDate(profile.createdAt, locale)}
            </InfoRow>
            <InfoRow
              label={t('admin:venue.f42.overview.updated', { defaultValue: 'Last updated' })}
            >
              {formatDate(profile.updatedAt, locale)}
            </InfoRow>
          </Section>

          {hoursEntries.length > 0 ? (
            <Section title={t('admin:venue.f42.overview.hours', { defaultValue: 'Opening hours' })}>
              <div className="grid grid-cols-1 gap-x-6 sm:grid-cols-2">
                {hoursEntries.map(([day, v]) => (
                  <InfoRow key={day} label={titleCase(day)}>
                    {formatHoursValue(v)}
                  </InfoRow>
                ))}
              </div>
            </Section>
          ) : null}
        </div>

        {/* Right — location map + photos (sticky so it stays in view) */}
        <div className="space-y-3 lg:sticky lg:top-4 lg:self-start">
          <Section title={t('admin:venue.f42.overview.location', { defaultValue: 'Location' })}>
            {latN != null && lngN != null && Number.isFinite(latN) && Number.isFinite(lngN) ? (
              <div className="overflow-hidden rounded-lg border border-hairline">
                <OsmMapPicker
                  lat={latN}
                  lng={lngN}
                  editable={false}
                  height={240}
                  ariaLabel={t('admin:venue.f42.overview.locationMap', {
                    defaultValue: 'Venue location',
                  })}
                />
              </div>
            ) : (
              <EmptyState
                title={t('admin:venue.f42.overview.noGeo', { defaultValue: 'No coordinates set' })}
              />
            )}
            <div className="mt-2 space-y-1">
              {regionLine ? <p className="text-[12.5px] text-ink">{regionLine}</p> : null}
              {profile.streetAddress ? (
                <p className="text-[12px] text-ink-3">{profile.streetAddress}</p>
              ) : null}
              {latN != null && lngN != null ? (
                <p className="font-mono text-[11px] text-ink-3">
                  {profile.lat}, {profile.lng}
                </p>
              ) : null}
            </div>
          </Section>

          <Section
            title={`${t('admin:venue.f42.overview.photos', { defaultValue: 'Photos' })} (${
              photos.length + profile.googlePhotos.length
            })`}
          >
            {photos.length === 0 && profile.googlePhotos.length === 0 ? (
              <EmptyState
                title={t('admin:venue.f42.overview.noPhotos', { defaultValue: 'No photos' })}
              />
            ) : (
              <div className="space-y-3">
                {photos.length > 0 ? (
                  <div className="grid grid-cols-3 gap-1.5">
                    {photos.slice(0, 9).map((p) => (
                      <div
                        key={p.id}
                        className="relative aspect-square overflow-hidden rounded-md bg-surface-2"
                      >
                        <img
                          src={p.cdnUrl ?? ''}
                          alt=""
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                        {p.moderationStatus !== 'approved' ? (
                          <span className="absolute bottom-0.5 start-0.5 rounded bg-panel/80 px-1 text-[9px] text-bg">
                            {p.moderationStatus}
                          </span>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : null}
                {profile.googlePhotos.length > 0 ? (
                  <div>
                    <div className="mb-1.5 flex items-center gap-1.5">
                      <Badge tone="info" size="sm">
                        {t('admin:venue.f42.overview.googlePhotos', { defaultValue: 'Google' })}
                      </Badge>
                      <span className="text-[11px] text-ink-3">{profile.googlePhotos.length}</span>
                    </div>
                    {showGooglePhotos ? (
                      <div className="grid grid-cols-3 gap-1.5">
                        {profile.googlePhotos.slice(0, 9).map((ref) => (
                          <a
                            key={ref}
                            href={googlePhotoUrl(ref, 1200)}
                            target="_blank"
                            rel="noreferrer"
                            className="relative block aspect-square overflow-hidden rounded-md bg-surface-2"
                          >
                            <img
                              src={googlePhotoUrl(ref, 400)}
                              alt=""
                              className="h-full w-full object-cover"
                              loading="lazy"
                            />
                          </a>
                        ))}
                      </div>
                    ) : (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => setShowGooglePhotos(true)}
                      >
                        {t('admin:venue.f42.overview.showGooglePhotos', {
                          defaultValue: 'Show Google photos',
                        })}
                      </Button>
                    )}
                  </div>
                ) : null}
              </div>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
}

// ─── Sports & courts editor (admin full control) ────────────────────────────

function VenueSportsCourtsEditor({
  profile,
  canEdit,
  onUpdated,
}: {
  profile: AdminVenueProfile;
  canEdit: boolean;
  onUpdated: (next: AdminVenueProfile) => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const baseline = profile.sports.map((s) => ({ sportName: s.sportName, isPrimary: s.isPrimary }));
  const [draftSports, setDraftSports] = useState(baseline);
  const [savingSports, setSavingSports] = useState(false);
  const [courtName, setCourtName] = useState('');
  const [courtSport, setCourtSport] = useState('');
  const [courtCapacity, setCourtCapacity] = useState('');
  const [courtBusy, setCourtBusy] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDraftSports(profile.sports.map((s) => ({ sportName: s.sportName, isPrimary: s.isPrimary })));
  }, [profile.sports]);

  const sportOptions = SportNameSchema.options;
  const available = sportOptions.filter((s) => !draftSports.some((d) => d.sportName === s));
  const sportsDirty =
    JSON.stringify(draftSports) !==
    JSON.stringify(profile.sports.map((s) => ({ sportName: s.sportName, isPrimary: s.isPrimary })));

  const fail = (err: unknown) => toast.push({ tone: 'danger', title: translateApiError(err, t) });

  const saveSports = async () => {
    setSavingSports(true);
    try {
      const res = await adminFetch<{ data: AdminVenueProfile }>(
        `/admin/venues/${profile.id}/sports`,
        { method: 'PUT', body: { sports: draftSports } },
      );
      onUpdated(res.data);
      toast.push({
        tone: 'success',
        title: t('admin:venue.f42.sports.saved', { defaultValue: 'Sports updated' }),
      });
    } catch (err) {
      fail(err);
    } finally {
      setSavingSports(false);
    }
  };

  const addCourt = async () => {
    if (!courtName.trim()) return;
    setCourtBusy(true);
    try {
      const res = await adminFetch<{ data: AdminVenueProfile }>(
        `/admin/venues/${profile.id}/courts`,
        {
          method: 'POST',
          body: {
            name: courtName.trim(),
            sportName: courtSport || null,
            capacity: courtCapacity ? Number(courtCapacity) : null,
          },
        },
      );
      onUpdated(res.data);
      setCourtName('');
      setCourtSport('');
      setCourtCapacity('');
      toast.push({
        tone: 'success',
        title: t('admin:venue.f42.courts.added', { defaultValue: 'Court added' }),
      });
    } catch (err) {
      fail(err);
    } finally {
      setCourtBusy(false);
    }
  };

  const mutateCourt = async (
    courtId: string,
    method: 'PATCH' | 'DELETE',
    body?: Record<string, unknown>,
  ) => {
    setCourtBusy(true);
    try {
      const res = await adminFetch<{ data: AdminVenueProfile }>(
        `/admin/venues/${profile.id}/courts/${courtId}`,
        { method, ...(body ? { body } : {}) },
      );
      onUpdated(res.data);
    } catch (err) {
      fail(err);
    } finally {
      setCourtBusy(false);
    }
  };

  return (
    <div className="space-y-3 pt-4" data-testid="admin-venue-sports-courts">
      <Section title={t('admin:venue.f42.sports.title', { defaultValue: 'Sports' })}>
        <div className="flex flex-wrap items-center gap-1.5">
          {draftSports.length === 0 ? (
            <span className="text-[12px] text-ink-3">
              {t('admin:venue.f42.sports.none', { defaultValue: 'No sports tagged.' })}
            </span>
          ) : (
            draftSports.map((s) => (
              <span
                key={s.sportName}
                className={`inline-flex items-center gap-1.5 rounded-full py-0.5 ps-2.5 pe-1 text-[11.5px] font-medium ${
                  s.isPrimary ? 'bg-accent-soft text-accent' : 'bg-surface-2 text-ink-2'
                }`}
              >
                <span
                  aria-hidden="true"
                  className="inline-flex size-6 shrink-0 [&_svg]:size-6 [&_svg]:shrink-0"
                >
                  <SportIcon name={s.sportName as SportName} size={24} />
                </span>
                {titleCase(s.sportName)}
                {canEdit ? (
                  <>
                    <button
                      type="button"
                      aria-label={t('admin:venue.f42.sports.makePrimary', {
                        defaultValue: 'Make primary',
                      })}
                      onClick={() =>
                        setDraftSports((p) =>
                          p.map((d) => ({ ...d, isPrimary: d.sportName === s.sportName })),
                        )
                      }
                      className={`ms-0.5 px-0.5 ${s.isPrimary ? 'text-accent' : 'text-ink-3 hover:text-accent'}`}
                    >
                      ★
                    </button>
                    <button
                      type="button"
                      aria-label={t('admin:venue.f42.sports.removeSport', {
                        defaultValue: 'Remove',
                      })}
                      onClick={() =>
                        setDraftSports((p) => p.filter((d) => d.sportName !== s.sportName))
                      }
                      className="px-0.5 text-ink-3 hover:text-danger"
                    >
                      ×
                    </button>
                  </>
                ) : s.isPrimary ? (
                  <span className="text-accent"> ★</span>
                ) : null}
              </span>
            ))
          )}
        </div>
        {canEdit ? (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <div className="min-w-[200px]">
              <Select
                value=""
                onChange={(e) => {
                  const v = e.target.value;
                  if (v) setDraftSports((p) => [...p, { sportName: v, isPrimary: p.length === 0 }]);
                }}
                options={[
                  {
                    value: '',
                    label: t('admin:venue.f42.sports.add', { defaultValue: 'Add a sport…' }),
                  },
                  ...available.map((s) => ({ value: s, label: titleCase(s) })),
                ]}
                data-testid="admin-venue-add-sport"
                aria-label={t('admin:venue.f42.sports.add', { defaultValue: 'Add a sport…' })}
              />
            </div>
            <Button
              variant="primary"
              size="sm"
              loading={savingSports}
              disabled={!sportsDirty}
              onClick={() => void saveSports()}
              data-testid="admin-venue-save-sports"
            >
              {t('admin:venue.f42.sports.save', { defaultValue: 'Save sports' })}
            </Button>
            <span className="text-[11px] text-ink-3">
              {t('admin:venue.f42.sports.primaryHint', {
                defaultValue: '★ marks the primary sport.',
              })}
            </span>
          </div>
        ) : null}
      </Section>

      <Section
        title={`${t('admin:venue.f42.courts.title', { defaultValue: 'Courts' })} (${profile.courts.length})`}
      >
        {profile.courts.length === 0 ? (
          <EmptyState title={t('admin:venue.f42.courts.none', { defaultValue: 'No courts yet' })} />
        ) : (
          <div className="space-y-1.5">
            {profile.courts.map((c) => (
              <div
                key={c.id}
                className="flex flex-wrap items-center gap-2 rounded-lg border border-hairline px-3 py-2"
              >
                <span className="text-[13px] font-medium text-ink">{c.name}</span>
                {c.sportName ? (
                  <Badge tone="neutral" size="sm">
                    {titleCase(c.sportName)}
                  </Badge>
                ) : null}
                {c.capacity ? (
                  <span className="text-[11px] text-ink-3">
                    {t('admin:venue.f42.courts.capacity', { defaultValue: 'Capacity' })}:{' '}
                    {c.capacity}
                  </span>
                ) : null}
                <Badge tone={c.status === 'active' ? 'success' : 'neutral'} size="sm">
                  {c.status}
                </Badge>
                {canEdit ? (
                  <div className="ms-auto flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="xs"
                      disabled={courtBusy}
                      onClick={() =>
                        void mutateCourt(c.id, 'PATCH', {
                          status: c.status === 'active' ? 'retired' : 'active',
                        })
                      }
                    >
                      {c.status === 'active'
                        ? t('admin:venue.f42.courts.retire', { defaultValue: 'Retire' })
                        : t('admin:venue.f42.courts.activate', { defaultValue: 'Activate' })}
                    </Button>
                    <Button
                      variant="ghost"
                      size="xs"
                      disabled={courtBusy}
                      onClick={() => void mutateCourt(c.id, 'DELETE')}
                      data-testid="admin-venue-delete-court"
                    >
                      {t('admin:common.delete', { defaultValue: 'Delete' })}
                    </Button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
        {canEdit ? (
          <div className="mt-3 flex flex-wrap items-end gap-2 border-t border-hairline pt-3">
            <div className="min-w-[160px]">
              <FormField label={t('admin:venue.f42.courts.name', { defaultValue: 'Court name' })}>
                <TextInput
                  value={courtName}
                  onChange={(e) => setCourtName(e.target.value)}
                  data-testid="admin-venue-court-name"
                />
              </FormField>
            </div>
            <div className="min-w-[150px]">
              <FormField
                label={t('admin:venue.f42.courts.sport', { defaultValue: 'Sport (optional)' })}
              >
                <Select
                  value={courtSport}
                  onChange={(e) => setCourtSport(e.target.value)}
                  options={[
                    { value: '', label: '—' },
                    ...sportOptions.map((s) => ({ value: s, label: titleCase(s) })),
                  ]}
                />
              </FormField>
            </div>
            <div className="w-[110px]">
              <FormField
                label={t('admin:venue.f42.courts.capacityField', { defaultValue: 'Capacity' })}
              >
                <TextInput
                  value={courtCapacity}
                  onChange={(e) => setCourtCapacity(e.target.value.replace(/[^0-9]/g, ''))}
                />
              </FormField>
            </div>
            <Button
              variant="secondary"
              size="sm"
              loading={courtBusy}
              disabled={!courtName.trim()}
              onClick={() => void addCourt()}
              data-testid="admin-venue-add-court"
            >
              {t('admin:venue.f42.courts.add', { defaultValue: 'Add court' })}
            </Button>
          </div>
        ) : null}
      </Section>
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────

export function AdminVenueDetailPage() {
  const { t, locale } = useI18n();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const auth = useAuth();

  const perms = auth.adminUser?.permissions ?? [];
  const canEdit = perms.includes('venues:admin_edit');
  const canWrite = perms.includes('venues:write');
  const canSuspend = perms.includes('venues:suspend');
  const canMerge = perms.includes('venues:merge');
  const canFlag = perms.includes('venues:flag');
  const canDelete = perms.includes('venues:delete');
  const canEditCancellation = perms.includes('bookings:edit_cancellation_policy');
  const canDecideClaims = perms.includes('venue_claims:review');
  const canViewCommission = perms.includes('commission_rate:read');
  // The Bookings tab embeds the shared admin-bookings list (`bookings:read` —
  // super_admin + support + finance); the Staff tab embeds the shared b2b
  // members list (`b2b_account_members:read` — super_admin + support). When the
  // actor lacks the permission the whole tab is hidden, so the embedded
  // component (and its API call) never mounts — no 403 path to handle.
  const canViewBookings = perms.includes('bookings:read');
  const canViewMembers = perms.includes('b2b_account_members:read');

  const [detail, setDetail] = useState<AdminVenueDetail | null>(null);
  const [profile, setProfile] = useState<AdminVenueProfile | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** V-12 — suspend/restore go through the standalone SuspendDrawer. */
  const [suspendDrawerMode, setSuspendDrawerMode] = useState<SuspendDrawerMode | null>(null);
  const [flagOpen, setFlagOpen] = useState(false);
  const [resolveFlagOpen, setResolveFlagOpen] = useState(false);
  const [resolveFlagNote, setResolveFlagNote] = useState('');
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [flagReason, setFlagReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchDetail = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      setDetail(await adminVenuesApi.getDetail(id));
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchDetail();
  }, [fetchDetail]);

  // Rich profile (description, contact, geo, sports, photos, hours) — fetched
  // best-effort; the page still renders its admin-ops shape if this fails. Also
  // re-fetched after an edit save (the edited scalar/jsonb fields live here).
  const refetchProfile = useCallback(() => {
    if (!id) return;
    adminFetch<{ data: AdminVenueProfile }>(`/admin/venues/${encodeURIComponent(id)}/profile`)
      .then((r) => setProfile(r.data))
      .catch(() => setProfile(null));
  }, [id]);

  useEffect(() => {
    refetchProfile();
  }, [refetchProfile]);

  const auditEvents = useMemo<readonly AuditEvent[]>(
    () => (detail ? detail.recentAudit.map((e) => auditToEvent(e, t)) : []),
    [detail, t],
  );

  /**
   * SuspendDrawer submit — toasts on success, toasts + rethrows on failure so
   * the drawer stays open for a retry (its submit() catches the rethrow).
   */
  const handleSuspendDrawerSubmit = async (input: { reason?: string }) => {
    if (!detail) return;
    try {
      if (suspendDrawerMode === 'suspend') {
        const next = await adminVenuesApi.suspend(detail.id, { reason: input.reason ?? '' });
        setDetail(next);
        toast.push({ tone: 'success', title: t('admin:venue.toast.suspendSuccess') });
      } else {
        const next = await adminVenuesApi.restore(
          detail.id,
          input.reason ? { reason: input.reason } : {},
        );
        setDetail(next);
        toast.push({
          tone: 'success',
          title: t('admin:venueMerge.suspendDrawer.toastRestored', {
            defaultValue: 'Venue restored',
          }),
        });
      }
    } catch (err) {
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
      throw err;
    }
  };

  /** V-08 — resolve the review flag (optional note recorded in the audit log). */
  const handleResolveFlag = async () => {
    if (!detail) return;
    setActionLoading(true);
    try {
      const note = resolveFlagNote.trim();
      const next = await adminVenuesApi.resolveFlag(detail.id, note ? { note } : {});
      setDetail(next);
      setResolveFlagOpen(false);
      setResolveFlagNote('');
      toast.push({
        tone: 'success',
        title: t('admin:venue.f42.flagResolve.success', { defaultValue: 'Flag resolved.' }),
      });
    } catch (err) {
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
    } finally {
      setActionLoading(false);
    }
  };

  /** V-16 — add an internal note (lands in venue_admin_audit → the timeline). */
  const handleAddNote = async () => {
    if (!detail) return;
    const note = noteText.trim();
    if (!note) return;
    setActionLoading(true);
    try {
      await adminVenuesApi.addNote(detail.id, { note });
      setNoteOpen(false);
      setNoteText('');
      toast.push({
        tone: 'success',
        title: t('admin:venue.f42.note.success', { defaultValue: 'Note added.' }),
      });
      await fetchDetail();
    } catch (err) {
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
    } finally {
      setActionLoading(false);
    }
  };

  const handleFlag = async () => {
    if (!detail) return;
    setActionLoading(true);
    try {
      const next = await adminVenuesApi.flag(detail.id, { reason: flagReason });
      setDetail(next);
      setFlagOpen(false);
      setFlagReason('');
      toast.push({ tone: 'success', title: t('admin:venue.f42.footer.flag') });
    } catch (err) {
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!detail) return;
    setActionLoading(true);
    try {
      await adminFetch(`/admin/venues/${encodeURIComponent(detail.id)}`, { method: 'DELETE' });
      toast.push({
        tone: 'success',
        title: t('admin:venue.f42.delete.success', { defaultValue: 'Venue deleted' }),
      });
      navigate('/venues');
    } catch (err) {
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size={32} />
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="space-y-4">
        <Banner tone="danger">{error ?? t('admin:errors.generic')}</Banner>
        <Button variant="ghost" onClick={() => navigate('/venues')}>
          {t('admin:common.back')}
        </Button>
      </div>
    );
  }

  const suspended = detail.status === 'suspended';
  const flagged = detail.flag.flaggedForReviewAt !== null;
  const visibility = venueVisibility(detail.status);
  const tabParam = searchParams.get('tab') ?? '';
  // A `?tab=` pointing at a permission-hidden tab falls back to `profile` instead
  // of a blank panel. Kept as plain boolean expressions (not a derived
  // `.filter` array) so React Compiler can still optimise this component.
  const tabHidden =
    (tabParam === 'bookings' && !canViewBookings) ||
    (tabParam === 'staff' && !canViewMembers) ||
    (tabParam === 'commission' && !canViewCommission);
  const activeTab = VENUE_TABS.includes(tabParam) && !tabHidden ? tabParam : 'profile';
  const handleTabChange = (v: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', v);
        return next;
      },
      { replace: true },
    );
  };

  return (
    <div className="space-y-5" data-testid="admin-venue-detail-page">
      {/* Header */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        {/* Left — title, slug, meta, public-visibility indicator */}
        <div className="min-w-0">
          <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5">
            <h1 className="text-h1 font-bold leading-tight text-ink">{detail.name}</h1>
            <span className="font-mono text-[12px] text-ink-3">/v/{detail.slug}</span>
          </div>
          {profile ? (
            <p className="mt-1 text-[12px] text-ink-3" data-testid="admin-venue-header-meta">
              {[profile.city.name, profile.governorate.name].filter(Boolean).join(', ') || '—'}
              {profile.createdByKind
                ? ` · ${CREATED_BY_LABEL[profile.createdByKind] ?? profile.createdByKind}`
                : ''}
              {` · ${t('admin:venue.f42.overview.added', { defaultValue: 'Added' })} ${formatDate(
                profile.createdAt,
                locale,
              )}`}
            </p>
          ) : null}
          <p
            className="mt-1.5 flex items-start gap-2 text-[12px] text-ink-2"
            data-testid="admin-venue-visibility"
          >
            <span
              className={`mt-1 inline-block h-2 w-2 shrink-0 rounded-full ${visibilityDotClass(
                visibility,
              )}`}
              aria-hidden="true"
            />
            <span>
              <span className="font-semibold text-ink">
                {visibility.isPublic
                  ? t('admin:venue.f42.visibility.publicLabel', { defaultValue: 'Public' })
                  : t('admin:venue.f42.visibility.hiddenLabel', {
                      defaultValue: 'Hidden from public',
                    })}
              </span>
              {` — ${t(visibility.key, { defaultValue: visibility.def })}`}
            </span>
          </p>
        </div>

        {/* Right — status badges + admin actions */}
        <div className="flex shrink-0 flex-col gap-2 lg:items-end">
          <div className="flex flex-wrap gap-2 lg:justify-end">
            <Badge tone={STATUS_TONES[detail.status] ?? 'neutral'}>
              {t(`admin:venue.status.${detail.status}`, { defaultValue: detail.status })}
            </Badge>
            <TierBadge
              tier={detail.tier as 'tier_1' | 'tier_2' | 'tier_3'}
              size="sm"
              label={t(`admin:venue.tier.${detail.tier}`, { defaultValue: detail.tier })}
            />
            <Badge tone={CLAIM_TONES[detail.claimStatus] ?? 'neutral'}>
              {t(`admin:venue.claimStatus.${detail.claimStatus}`, {
                defaultValue: detail.claimStatus,
              })}
            </Badge>
            {/* F09 v2 (§13 row 21) — tick + owner state from `verificationState`. */}
            <VerificationStateCell
              entityType="venue"
              state={detail.verificationState}
              openRequest={detail.openVerificationRequest}
              testId="admin-venue-detail-verification-badge"
            />
            {flagged ? <Badge tone="danger">{t('admin:venue.f42.badge.flagged')}</Badge> : null}
            {profile?.source ? (
              <Badge tone="info" data-testid="admin-venue-source-badge">
                {SOURCE_LABEL[profile.source] ?? profile.source}
              </Badge>
            ) : null}
          </div>
          {canSuspend || canFlag || canMerge || canDelete ? (
            <div className="flex flex-wrap gap-2 lg:justify-end" data-testid="admin-venue-footer">
              {canSuspend && !suspended ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setSuspendDrawerMode('suspend')}
                  data-testid="admin-venue-footer-suspend"
                >
                  {t('admin:venue.f42.footer.suspend')}
                </Button>
              ) : null}
              {canSuspend && suspended ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setSuspendDrawerMode('restore')}
                  data-testid="admin-venue-footer-restore"
                >
                  {t('admin:venue.f42.footer.restore')}
                </Button>
              ) : null}
              {canFlag && !flagged ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setFlagOpen(true)}
                  data-testid="admin-venue-footer-flag"
                >
                  {t('admin:venue.f42.footer.flag')}
                </Button>
              ) : null}
              {canFlag && flagged ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setResolveFlagNote('');
                    setResolveFlagOpen(true);
                  }}
                  data-testid="admin-venue-footer-resolve-flag"
                >
                  {t('admin:venue.f42.footer.resolveFlag')}
                </Button>
              ) : null}
              {canMerge ? (
                <Link to={`/venues/merge?source=${encodeURIComponent(detail.id)}`}>
                  <Button variant="secondary" size="sm" data-testid="admin-venue-footer-merge">
                    {t('admin:venue.f42.footer.merge')}
                  </Button>
                </Link>
              ) : null}
              {canDelete && (detail.status === 'draft' || detail.status === 'suspended') ? (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    setDeleteConfirm('');
                    setDeleteOpen(true);
                  }}
                  data-testid="admin-venue-footer-delete"
                >
                  {t('admin:venue.f42.footer.delete', { defaultValue: 'Delete venue' })}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {suspended ? (
        <div data-testid="admin-venue-detail-suspended-banner">
          <Banner tone="warning">
            {t('admin:venue.f42.detail.suspendedBanner', {
              date: formatDate(detail.suspension.suspendedAt, locale),
              reason: detail.suspension.suspensionReason ?? '—',
            })}
          </Banner>
        </div>
      ) : null}

      {flagged ? (
        <div data-testid="admin-venue-detail-flagged-banner">
          <Banner tone="danger">
            {t('admin:venue.f42.detail.flaggedBanner', {
              date: formatDate(detail.flag.flaggedForReviewAt, locale),
              reason: detail.flag.flagReason ?? '—',
            })}
          </Banner>
        </div>
      ) : null}

      {/* Tabs — synced to ?tab= so a refresh restores the open tab */}
      <Tabs value={activeTab} defaultValue="profile" onValueChange={handleTabChange}>
        <TabList>
          <Tab value="profile">{t('admin:venue.f42.tabs.profile')}</Tab>
          <Tab value="sportsCourts">
            {t('admin:venue.f42.tabs.sportsCourts', { defaultValue: 'Sports & Courts' })}
          </Tab>
          <Tab value="cancellationPolicy">
            {t('admin:venue.f42.tabs.cancellationPolicy', { defaultValue: 'Cancellation policy' })}
          </Tab>
          {canViewBookings ? (
            <Tab value="bookings">{t('admin:venue.f42.tabs.bookings')}</Tab>
          ) : null}
          {canViewMembers ? <Tab value="staff">{t('admin:venue.f42.tabs.staff')}</Tab> : null}
          <Tab value="deepLinks">{t('admin:venue.f42.tabs.deepLinks')}</Tab>
          {canViewCommission ? (
            <Tab value="commission">
              {t('admin:venue.f42.tabs.commission', { defaultValue: 'Commission' })}
            </Tab>
          ) : null}
          <Tab value="audit">{t('admin:venue.f42.tabs.auditLog')}</Tab>
          <Tab value="claims">{t('admin:venue.f42.tabs.claims')}</Tab>
        </TabList>

        <TabPanel value="profile">
          {canEdit ? (
            <div className="pt-3" data-testid="admin-venue-profile-tab">
              <div className="flex items-center justify-between gap-3 border-b border-hairline pb-2.5">
                <h2 className="text-[14px] font-semibold text-ink">
                  {editMode
                    ? t('admin:venue.f42.edit.editingDetails', { defaultValue: 'Editing details' })
                    : t('admin:venue.f42.tabs.profile', { defaultValue: 'Profile' })}
                </h2>
                {editMode ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditMode(false)}
                    data-testid="admin-venue-edit-cancel"
                  >
                    {t('admin:common.cancel', { defaultValue: 'Cancel' })}
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setEditMode(true)}
                    data-testid="admin-venue-edit-toggle"
                  >
                    {t('admin:venue.f42.edit.editDetails', { defaultValue: 'Edit details' })}
                  </Button>
                )}
              </div>
              {editMode ? (
                <div className="pt-4">
                  <VenueEditForm
                    key={`${detail.id}:${detail.updatedAt}`}
                    detail={detail}
                    profile={profile}
                    canEdit={canEdit}
                    onSaved={(next) => {
                      setDetail(next);
                      setEditMode(false);
                    }}
                    onProfileRefetch={refetchProfile}
                    onReload={() => {
                      void fetchDetail();
                      refetchProfile();
                    }}
                  />
                </div>
              ) : profile ? (
                <VenueOverview profile={profile} />
              ) : (
                <div className="pt-4">
                  <Banner tone="info">
                    {t('admin:venue.f42.detail.loadingProfile', { defaultValue: 'Loading…' })}
                  </Banner>
                </div>
              )}
            </div>
          ) : profile ? (
            <div className="pt-3">
              <VenueOverview profile={profile} />
            </div>
          ) : (
            <div className="pt-4" data-testid="admin-venue-profile-readonly">
              <Banner tone="info">{t('admin:venue.f42.detail.noPermission')}</Banner>
            </div>
          )}
          {canWrite ? (
            <div className="pt-3">
              <StatusTierAdminSection
                key={`${detail.id}:${detail.updatedAt}:${detail.status}:${detail.tier}:${detail.claimStatus}:${detail.slug}`}
                detail={detail}
                onChanged={() => {
                  void fetchDetail();
                  refetchProfile();
                }}
              />
            </div>
          ) : null}
        </TabPanel>

        <TabPanel value="sportsCourts">
          {profile ? (
            <VenueSportsCourtsEditor
              profile={profile}
              canEdit={canEdit}
              onUpdated={(next) => setProfile(next)}
            />
          ) : (
            <div className="pt-4">
              <Banner tone="info">
                {t('admin:venue.f42.detail.loadingProfile', { defaultValue: 'Loading…' })}
              </Banner>
            </div>
          )}
        </TabPanel>

        <TabPanel value="cancellationPolicy">
          <VenueCancellationPolicyTab
            key={`${detail.id}:${detail.updatedAt}`}
            detail={detail}
            canEdit={canEditCancellation}
            onSaved={(next) => setDetail(next)}
            onReload={() => {
              void fetchDetail();
            }}
          />
        </TabPanel>

        {/* Bookings — embeds the SAME AdminBookingsList the standalone /bookings
            page renders, scoped to this venue (venue filter hidden). Gated +
            hidden when the actor lacks bookings:read (so the list never 403s). */}
        {canViewBookings ? (
          <TabPanel value="bookings">
            <div className="space-y-3 pt-4" data-testid="admin-venue-bookings-tab">
              <h3 className="text-[13px] font-semibold text-ink">
                {t('admin:venue.f42.readThrough.bookingsTitle')}
              </h3>
              <AdminBookingsList lockedVenueId={detail.id} />
            </div>
          </TabPanel>
        ) : null}

        {/* Staff — embeds the SAME B2bAccountMembersTab the b2b-account detail
            page renders, scoped to members who can operate this venue. Gated +
            hidden when the actor lacks b2b_account_members:read. */}
        {canViewMembers ? (
          <TabPanel value="staff">
            <div className="space-y-3 pt-4" data-testid="admin-venue-staff-tab">
              <h3 className="text-[13px] font-semibold text-ink">
                {t('admin:venue.f42.readThrough.staffTitle')}
              </h3>
              {detail.deepLinks.staffAccountId ? (
                <B2bAccountMembersTab
                  accountId={detail.deepLinks.staffAccountId}
                  venueId={detail.id}
                />
              ) : (
                <EmptyState title={t('admin:venue.f42.readThrough.staffEmpty')} />
              )}
            </div>
          </TabPanel>
        ) : null}

        <TabPanel value="deepLinks">
          <div
            className="grid grid-cols-1 gap-2.5 pt-4 sm:grid-cols-2"
            data-testid="admin-venue-deeplinks-tab"
          >
            {/* F09 v2 (E41) — the Verification card: state + deep links to the
                entity page and the open request (`deepLinks.verification`). */}
            <EntityVerificationCard
              entityType="venue"
              entityId={detail.id}
              verified={detail.verificationState === 'verified'}
              state={detail.verificationState ?? 'not_requested'}
              openRequest={detail.openVerificationRequest}
              available={detail.deepLinks.verification ?? true}
              testId="admin-venue-verification-card"
            />
            <DeepLinkCard
              id="subscription"
              title={t('admin:venue.f42.deepLinks.subscription')}
              description={t('admin:venue.f42.deepLinks.subscriptionDesc')}
              href={`/venue-subscriptions/${encodeURIComponent(detail.id)}`}
              available={detail.deepLinks.subscription}
            />
            <DeepLinkCard
              id="placements"
              title={t('admin:venue.f42.deepLinks.placements')}
              description={t('admin:venue.f42.deepLinks.placementsDesc')}
              href={`/placements?venueId=${encodeURIComponent(detail.id)}`}
              available={detail.deepLinks.placements}
            />
            <DeepLinkCard
              id="promotions"
              title={t('admin:venue.f42.deepLinks.promotions')}
              description={t('admin:venue.f42.deepLinks.promotionsDesc')}
              href={`/promotions?venueId=${encodeURIComponent(detail.id)}`}
              available={detail.deepLinks.promotions}
            />
            {/* V-05 — inline hosted-tournaments list (each row links to the
                real /tournaments/:id route) instead of the dead deep-link. */}
            <HostedTournamentsCard venueId={detail.id} />
            {/* BP-07 — per-venue payment config (F21 admin pages). FIX 5.22
                (§13.11-6) — a content_moderator holds payment_config:write but
                not payments:read, so it goes straight to the override page
                (which reads via the /override-view endpoint). */}
            <DeepLinkCard
              id="payment-config"
              title={t('admin:venue.f42.deepLinks.paymentConfig', {
                defaultValue: 'Payment config',
              })}
              description={t('admin:venue.f42.deepLinks.paymentConfigDesc', {
                defaultValue: 'Accepted methods, payout instructions & overrides',
              })}
              href={
                perms.includes('payments:read')
                  ? `/venues/${encodeURIComponent(detail.id)}/payment-config`
                  : `/venues/${encodeURIComponent(detail.id)}/payment-config/override`
              }
              available={perms.includes('payments:read') || perms.includes('payment_config:write')}
            />
            {/* F44 — the reserved financials deep-link now points at the
                Finance settlements view filtered to this venue. */}
            <DeepLinkCard
              id="financials"
              title={t('admin:venue.f42.deepLinks.financials')}
              description={t('admin:venue.f42.deepLinks.financialsDesc')}
              href={`/finance/settlements?entityId=${encodeURIComponent(detail.id)}`}
              available={detail.deepLinks.financials}
            />
          </div>
        </TabPanel>

        {/* CST-003 — the F23 Commission tab, remounted after the F42 rewrite
            orphaned it (rate history + statements for this venue). */}
        {canViewCommission ? (
          <TabPanel value="commission">
            <div className="pt-4" data-testid="admin-venue-commission-tab">
              <AdminVenueCommissionTab venueId={detail.id} />
            </div>
          </TabPanel>
        ) : null}

        <TabPanel value="audit">
          <div className="space-y-3 pt-4" data-testid="admin-venue-audit-tab">
            {canFlag ? (
              <div className="flex justify-end">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setNoteText('');
                    setNoteOpen(true);
                  }}
                  data-testid="admin-venue-add-note"
                >
                  {t('admin:venue.f42.footer.addNote')}
                </Button>
              </div>
            ) : null}
            {auditEvents.length === 0 ? (
              <EmptyState title={t('admin:venue.f42.audit.empty')} />
            ) : (
              <AuditLogTimeline events={auditEvents} />
            )}
          </div>
        </TabPanel>

        <TabPanel value="claims">
          <div className="space-y-3 pt-4" data-testid="admin-venue-claims-tab">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <Stat
                label={t('admin:venue.f42.claims.pendingLabel', {
                  defaultValue: 'Pending claims',
                })}
                value={String(detail.claimSummary.pendingCount)}
              />
              <Stat
                label={t('admin:venue.f42.claims.latestLabel', {
                  defaultValue: 'Latest claim status',
                })}
                value={detail.claimSummary.latestStatus ?? '—'}
              />
            </div>
            <Link to={`/venue-claims?venueId=${encodeURIComponent(detail.id)}`}>
              <Button variant="secondary" size="sm" data-testid="admin-venue-claims-link">
                {t('admin:venue.f42.claims.openQueue')}
              </Button>
            </Link>
            {/* V-12 — the venue-scoped claim review surface (approve / reject /
                revoke + presigned doc download), previously orphaned. */}
            <ClaimsTab venueId={detail.id} canDecide={canDecideClaims} />
          </div>
        </TabPanel>
      </Tabs>

      {/* Suspend / restore drawer (V-12 — the standalone SuspendDrawer with the
          F26 owner-notification preview + confirm; V-19 — proper restore copy). */}
      <SuspendDrawer
        open={suspendDrawerMode !== null}
        mode={suspendDrawerMode ?? 'suspend'}
        venueName={detail.name}
        onClose={() => setSuspendDrawerMode(null)}
        onSubmit={handleSuspendDrawerSubmit}
      />

      {/* Flag modal — confirm gated on FLAG_REASON_MIN (V-18; the shared Dialog
          has no confirm-disable prop, so this composes Modal directly). */}
      <Modal
        open={flagOpen}
        onClose={() => setFlagOpen(false)}
        title={t('admin:venue.f42.footer.flag')}
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setFlagOpen(false)} disabled={actionLoading}>
              {t('admin:common.cancel')}
            </Button>
            <Button
              variant="destructive"
              loading={actionLoading}
              disabled={flagReason.trim().length < FLAG_REASON_MIN}
              onClick={() => void handleFlag()}
              data-testid="admin-venue-flag-confirm"
            >
              {t('admin:venue.f42.footer.flag')}
            </Button>
          </>
        }
      >
        <FormField
          label={t('admin:venue.fields.reason')}
          helper={t('admin:venue.f42.flagReasonHelper', {
            defaultValue: 'Required — min 5 characters; recorded in the audit log.',
          })}
          required
        >
          <Textarea
            rows={3}
            maxLength={2000}
            value={flagReason}
            onChange={(e) => setFlagReason(e.target.value)}
            data-testid="admin-venue-flag-reason"
          />
        </FormField>
      </Modal>

      {/* Resolve-flag dialog (V-08 — optional note recorded in the audit log). */}
      <Dialog
        open={resolveFlagOpen}
        onClose={() => setResolveFlagOpen(false)}
        onConfirm={() => void handleResolveFlag()}
        title={t('admin:venue.f42.flagResolve.title', { defaultValue: 'Resolve this flag?' })}
        description={t('admin:venue.f42.flagResolve.body', {
          defaultValue:
            'Marks the review flag on this venue as resolved. The flag history stays in the audit log.',
        })}
        confirmLabel={t('admin:venue.f42.footer.resolveFlag')}
        cancelLabel={t('admin:common.cancel')}
        loading={actionLoading}
      >
        <FormField
          label={t('admin:venue.f42.flagResolve.noteLabel', {
            defaultValue: 'Resolution note (optional)',
          })}
        >
          <Textarea
            rows={3}
            maxLength={2000}
            value={resolveFlagNote}
            onChange={(e) => setResolveFlagNote(e.target.value)}
            data-testid="admin-venue-resolve-flag-note"
          />
        </FormField>
      </Dialog>

      {/* Add internal note (V-16 — surfaces in the audit timeline). */}
      <Modal
        open={noteOpen}
        onClose={() => setNoteOpen(false)}
        title={t('admin:venue.f42.footer.addNote')}
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setNoteOpen(false)} disabled={actionLoading}>
              {t('admin:common.cancel')}
            </Button>
            <Button
              variant="primary"
              loading={actionLoading}
              disabled={noteText.trim().length === 0}
              onClick={() => void handleAddNote()}
              data-testid="admin-venue-add-note-confirm"
            >
              {t('admin:venue.f42.note.save', { defaultValue: 'Add note' })}
            </Button>
          </>
        }
      >
        <FormField
          label={t('admin:venue.f42.note.label', { defaultValue: 'Internal note' })}
          helper={t('admin:venue.f42.note.helper', {
            defaultValue: 'Visible to admins only — recorded in the venue audit log.',
          })}
          required
        >
          <Textarea
            rows={4}
            maxLength={2000}
            showCounter
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            data-testid="admin-venue-note-text"
          />
        </FormField>
      </Modal>

      {/* Delete (hard, draft-only) — type-to-confirm */}
      <Modal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title={t('admin:venue.f42.delete.title', { defaultValue: 'Delete this venue?' })}
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleteOpen(false)} disabled={actionLoading}>
              {t('admin:common.cancel')}
            </Button>
            <Button
              variant="destructive"
              loading={actionLoading}
              disabled={deleteConfirm.trim().toUpperCase() !== 'DELETE'}
              onClick={() => void handleDelete()}
              data-testid="admin-venue-delete-confirm-btn"
            >
              {t('admin:venue.f42.delete.confirm', { defaultValue: 'Delete permanently' })}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Banner tone="danger">
            {detail.status === 'suspended'
              ? t('admin:venue.f42.delete.warningSuspended', {
                  defaultValue:
                    'This permanently deletes this venue AND all of its bookings, payments, commission records, reviews and history. This cannot be undone — prefer keeping it suspended unless you are sure.',
                })
              : t('admin:venue.f42.delete.warning', {
                  defaultValue:
                    'This permanently removes the draft venue and all its data. This cannot be undone.',
                })}
          </Banner>
          <p className="text-[13px] font-medium text-ink">{detail.name}</p>
          <FormField
            label={t('admin:venue.f42.delete.typeConfirm', {
              defaultValue: 'Type DELETE to confirm',
            })}
          >
            <TextInput
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              data-testid="admin-venue-delete-confirm"
            />
          </FormField>
        </div>
      </Modal>
    </div>
  );
}
